// src/pages/VanessaPage.tsx — Site officiel Vanessa : qui est Vanessa
import { MessageCircle, Mic, Camera, Brain, Sparkles, Layers, ShieldCheck, Clock, Check, X } from 'lucide-react';
import { Section, Grid, FeatureCard, CtaBand, PageHeader, Container } from '@/components/site/blocks';
import { buttonClasses } from '@/components/ui/button';
import { APP_OPEN_URL } from '@/config/site';

const MOODS = [
    { name: 'Curieuse', text: 'Elle pose des questions, relance, veut connaître la suite.' },
    { name: 'Moqueuse', text: 'Elle taquine avec humour quand le sujet s’y prête.' },
    { name: 'Détective', text: 'Elle recoupe, doute et demande des preuves.' },
    { name: 'Complice', text: 'Elle rit avec vous, sans jamais se moquer de vous.' },
    { name: 'Confidente', text: 'Elle écoute et devient plus posée quand c’est sérieux.' },
];

export default function VanessaPage() {
    return (
        <>
            <PageHeader
                eyebrow="Vanessa"
                title="Celle qui vous ressemble et vous rassemble."
                intro="Vanessa est l’intelligence artificielle qui donne son nom à la plateforme. Une personnalité au langage de rue, pas un assistant classique : elle écoute, relance, plaisante, et reste sérieuse sur les sujets sensibles."
            >
                <a href={APP_OPEN_URL} className={buttonClasses('primary', 'lg')}>Discuter avec Vanessa</a>
            </PageHeader>

            <Section title="Comment elle vous parle" intro="Des réponses courtes et vivantes, comme dans une vraie conversation de chat, pas des paragraphes de manuel.">
                <div className="grid gap-5 md:grid-cols-2">
                    <div className="rounded-2xl border border-gray-200 bg-white p-6">
                        <p className="mb-3 inline-flex items-center gap-2 font-display font-semibold text-ink">
                            <Check className="h-5 w-5 text-ochre" aria-hidden="true" /> Vanessa
                        </p>
                        <p className="rounded-2xl bg-brand-tint p-4 text-ink">
                            Attends, attends. Il a vraiment dit ça devant tout le monde ? Et toi, tu as répondu quoi ?
                        </p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white p-6">
                        <p className="mb-3 inline-flex items-center gap-2 font-display font-semibold text-ink">
                            <X className="h-5 w-5 text-gray-600" aria-hidden="true" /> Pas un assistant classique
                        </p>
                        <p className="rounded-2xl bg-sand p-4 text-gray-700">
                            « Je suis un modèle de langage. Voici une liste de cinq conseils pour gérer un conflit en milieu professionnel… »
                        </p>
                    </div>
                </div>
            </Section>

            <Section tone="sand" title="Ce qu’elle sait faire">
                <Grid cols={3}>
                    <FeatureCard icon={MessageCircle} title="Texte">Une conversation naturelle, avec son ton.</FeatureCard>
                    <FeatureCard icon={Mic} title="Vocal">Envoyez un vocal, elle peut répondre à voix haute.</FeatureCard>
                    <FeatureCard icon={Camera} title="Photo">Elle commente la situation ou l’ambiance, jamais le physique d’une personne.</FeatureCard>
                    <FeatureCard icon={Brain} title="Mémoire">
                        Elle retient certains éléments d’une conversation à l’autre. Vous consultez et effacez ce qu’elle a retenu à tout moment depuis votre profil.
                    </FeatureCard>
                    <FeatureCard icon={Layers} title="Connecteurs partenaires">
                        Une conversation peut être liée à un partenaire (association, média). Une pastille l’indique en haut du chat, et Vanessa reste factuellement rigoureuse sur son sujet.
                    </FeatureCard>
                    <FeatureCard icon={Clock} title="Limite quotidienne">
                        Pour qu’elle reste disponible pour tout le monde, le nombre d’échanges par jour est limité. Si vous l’atteignez, elle vous le dit elle-même et vous donne rendez-vous le lendemain.
                    </FeatureCard>
                </Grid>
            </Section>

            <Section title="Ses humeurs" intro="Elle s’adapte au ton de la conversation. Pas besoin de lui demander de changer.">
                <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {MOODS.map((m) => (
                        <li key={m.name} className="rounded-2xl border border-gray-200 bg-white p-5">
                            <Sparkles className="mb-2 h-5 w-5 text-ochre" aria-hidden="true" />
                            <p className="font-display text-lg font-semibold text-ink">{m.name}</p>
                            <p className="mt-1 text-base text-gray-700">{m.text}</p>
                        </li>
                    ))}
                </ul>
            </Section>

            <Section tone="sand">
                <Container className="!px-0">
                    <div className="flex flex-col gap-4 rounded-3xl bg-white p-8 sm:flex-row sm:items-start">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-tint text-ochre">
                            <ShieldCheck className="h-6 w-6" aria-hidden="true" />
                        </span>
                        <div>
                            <h2 className="font-display text-2xl font-bold text-ink">Une IA, et elle le dit</h2>
                            <p className="mt-2 max-w-2xl text-lg text-gray-700">
                                Vanessa ne se fait pas passer pour une personne réelle. Elle ne tranche pas à la place de la justice, ne diffame personne et change de ton face à une détresse. Tout est détaillé sur la page Confiance et sécurité.
                            </p>
                        </div>
                    </div>
                </Container>
            </Section>

            <CtaBand
                title="Dites-lui bonjour"
                text="Une conversation suffit pour comprendre."
                primary={{ label: 'Ouvrir l’application', href: APP_OPEN_URL }}
                secondary={{ label: 'Confiance et sécurité', to: '/confiance', variant: 'secondary' }}
            />
        </>
    );
}
